import gql from 'graphql-tag';
import {
	findIndex,
	pick,
	differenceBy,
	keys,
	isArray,
	isFunction
} from 'lodash';

import { defaults as allDefaults } from 'model';
import {
	useCreateRiskMutation,
	useUpdateRiskMutation,
	useDeleteRiskMutation,
	useUpdateRiskScenarioMutation,
	useCreateTaggingMutation,
	useDeleteTaggingMutation,
	useCreateRiskStakeImpactMutation,
	useDeleteRiskStakeImpactMutation,
	useUpdateRiskStakeImpactStrengthMutation,
	useCreateRiskOpeningLinkMutation,
	useDeleteRiskOpeningLinkMutation,
	useUpdateRiskOpeningLinkStrengthMutation
} from 'graph/dist/generated';
import {
	getFieldFunctions,
	getFragmentFields,
	optimisticlyUpdateScenario,
	hideElementInOtherScenarios
} from 'utils/graphql';
import { useXImpactController } from './useXImpact';

export const useRiskController = () => {
	const [createRisk] = useCreateRiskMutation();
	const [updateRisk] = useUpdateRiskMutation();
	const [deleteRisk] = useDeleteRiskMutation();
	const [updateRiskScenario] = useUpdateRiskScenarioMutation();
	const [createTagging] = useCreateTaggingMutation();
	const [deleteTagging] = useDeleteTaggingMutation();

	const riskImpactController = useXImpactController();
	const [createRiskStakeImpact] = useCreateRiskStakeImpactMutation();
	const [deleteRiskStakeImpact] = useDeleteRiskStakeImpactMutation();
	const [updateRiskStakeImpactStrength] =
		useUpdateRiskStakeImpactStrengthMutation();
	const [createRiskOpeningLink] = useCreateRiskOpeningLinkMutation();
	const [deleteRiskOpeningLink] = useDeleteRiskOpeningLinkMutation();
	const [updateRiskOpeningLinkStrength] =
		useUpdateRiskOpeningLinkStrengthMutation();

	// --- RiskOpeningLink (n-n, tasks #158-#163) ---------------------------
	// Cut-over E1 (2026-06-11) : la table de liens est la seule source de
	// vérité. matchingOpportunityId n'est plus ni lu ni écrit (champ
	// @deprecated, purge en E2).
	const getOpeningIds = (risk: any): string[] =>
		(risk?.openingLinks || []).map((l: any) => l.openingId);

	const handleCreateOpeningLink = async (riskId: string, openingId: string) => {
		return await createRiskOpeningLink({
			variables: { riskId, openingId },
			update(cache, { data }) {
				const created = data?.createRiskOpeningLink;
				if (!created?.id) return;
				cache.modify({
					id: `Risk:${riskId}`,
					fields: {
						openingLinks(existing = []) {
							const newLink = cache.writeFragment({
								id: 'RiskOpeningLink:' + created.id,
								data: created,
								fragment: gql`
									fragment RiskOpeningLinkFragment on RiskOpeningLink {
										id
										riskId
										openingId
										strength
										createdAt
									}
								`
							});
							return [...existing, newLink];
						}
					}
				});
			}
		});
	};

	const handleDeleteOpeningLink = async (id: string, riskId: string) => {
		return await deleteRiskOpeningLink({
			variables: { id },
			update(cache) {
				cache.modify({
					id: `Risk:${riskId}`,
					fields: {
						openingLinks(existing = [], { readField }) {
							return existing.filter(
								(ref: any) => readField('id', ref) !== id
							);
						}
					}
				});
			},
			optimisticResponse: { deleteRiskOpeningLink: true }
		});
	};

	// Met a jour l'intensite d'un lien risk<->opening (strength 1-3) — mire
	// updateStakeImpactStrength. Patch optimiste du cache sur l'entite lien.
	const handleUpdateOpeningLinkStrength = async (
		link: any,
		strength: number
	) => {
		if (!link?.id) return;
		return await updateRiskOpeningLinkStrength({
			variables: { id: link.id, strength },
			update(cache) {
				cache.modify({
					id: `RiskOpeningLink:${link.id}`,
					fields: { strength: () => strength }
				});
			},
			optimisticResponse: { updateRiskOpeningLinkStrength: true }
		});
	};

	// Cycle 1->2->3->suppression sur le lien d'une opening pour un risque
	// donne (mire le cycle des autres relations). riskId = cote ou le lien
	// est porte (peut etre le risque OU l'opening selon la section UI).
	const cycleOpeningLink = async (risk: any, openingId: string) => {
		if (!risk?.id) return;
		const link = (risk.openingLinks || []).find(
			(l: any) => l.openingId === openingId
		);
		if (!link) {
			return await handleCreateOpeningLink(risk.id, openingId);
		}
		const current = link.strength || 1;
		if (current < 3) {
			return await handleUpdateOpeningLinkStrength(link, current + 1);
		}
		return await handleDeleteOpeningLink(link.id, risk.id);
	};

	// Diff les liens d'un risque vers la liste cible d'openingIds.
	const syncOpeningLinks = async (risk: any, openingIds: string[]) => {
		if (!risk?.id) return;
		const current: any[] = risk.openingLinks || [];
		const currentIds = current.map((l) => l.openingId);
		const toAdd = openingIds.filter((oid) => !currentIds.includes(oid));
		const toRemove = current.filter((l) => !openingIds.includes(l.openingId));

		for (const oid of toAdd) {
			await handleCreateOpeningLink(risk.id, oid);
		}
		for (const link of toRemove) {
			await handleDeleteOpeningLink(link.id, risk.id);
		}
	};

	const updateTaggings = async (risk, tagIds) => {
		if (!tagIds || !isArray(tagIds)) return;

		const oldTaggings = risk.taggings || [];
		const newTaggings = tagIds.map((tag) => ({
			tagId: tag,
			objectId: risk.id
		}));

		const toDelete = differenceBy(oldTaggings, newTaggings, 'tagId');
		const toAdd = differenceBy(newTaggings, oldTaggings, 'tagId');

		toDelete.forEach(async (tagging) => {
			await deleteTagging({
				variables: {
					...tagging
				},
				update(cache, { data: { deleteTagging } }) {
					cache.modify({
						id: `Risk:${tagging.objectId}`,
						fields: {
							taggings(taggings = []) {
								return taggings.filter(
									(t) =>
										t.__ref !==
										`Tagging:${deleteTagging.id}`
								);
							}
						}
					});
				}
			});
		});

		toAdd.forEach(async (tagging) => {
			const variables = {
				projectId: risk.projectId,
				...tagging,
				type: 'risk'
			};
			await createTagging({
				variables,
				update(cache, { data: { createTagging } }) {
					cache.modify({
						id: `Risk:${tagging.objectId}`,
						fields: {
							taggings(taggings = []) {
								const newTagging = cache.writeFragment({
									id: 'Tagging:' + createTagging.id,
									data: createTagging,
									fragment: gql`
										fragment TaggingFragment on ${'Tagging'} {
											${getFragmentFields(variables)}
										}
									`
								});

								return [...taggings, newTagging];
							}
						}
					});
				}
			});
		});
	};

	const handleCreateRisk = async (
		projectId: string,
		risks,
		values: any,
		scenario = null,
		defaults = {},
		scenarios: any[] = []
	) => {
		// Bug Marco 2026-05-27: an upstream caller (UIProvider iterating over a
		// message.items array) could pass undefined here, crashing on
		// values.evaluatedAt. Skip silently instead — better than burning the
		// whole subscription handler.
		if (!values || typeof values !== 'object') {
			console.warn('handleCreateRisk: values is missing or not an object, skipping');
			return;
		}

		let appliedDefaults = {
			...allDefaults.Risk,
			...defaults
		};

		let evaluatedAt = values.evaluatedAt || appliedDefaults.evaluatedAt;
		evaluatedAt =
			!values.evaluatedAt || !isFunction(values.evaluatedAt?.toISOString)
				? ''
				: new Date(values.evaluatedAt).toISOString();

		let variables = {
			projectId: projectId,
			...appliedDefaults,
			name: values.name,
			reference: values.reference || '',
			impact: values.impact || appliedDefaults.impact,
			riskCategoryId:
				values.riskCategoryId || appliedDefaults.riskCategoryId,
			isOpportunity:
				values.isOpportunity || appliedDefaults.isOpportunity,
			likelihood: values.likelihood || appliedDefaults.likelihood,
			description: values.description || appliedDefaults.description,
			taggings: [],
			scenarios: [],
			createdAt: new Date().toISOString(),
			evaluatedAt: evaluatedAt,
			// Si on est dans un scénario, créer directement désactivé dans la baseline
			enabled: scenario ? false : true
		};

		let result = await createRisk({
			variables,
			update(cache, { data: { createRisk } }) {
				cache.modify({
					id: `Project:${projectId}`,
					fields: {
						risks(risks = []) {
							const newRisk = cache.writeFragment({
								id: 'Risk:' + createRisk.id,
								data: createRisk,
								fragment: gql`
									fragment RiskFragment on Risk {
										${getFragmentFields(allDefaults.Risk)}
									}
								`
							});

							return [...risks, newRisk];
						}
					}
				});
			},
			optimisticResponse: {
				createRisk: {
					id: 'temp-id',
					...appliedDefaults,
					...variables,
					impactedStakes: [],
					openingLinks: []
				}
			}
		});

		await updateTaggings(result.data.createRisk, values.taggings);

		if (values.impactedStakes !== undefined) {
			console.log(
				'updating impactedStakes',
				values.impactedStakes
			);
			await riskImpactController.diff(
				{
					create: createRiskStakeImpact,
					delete: deleteRiskStakeImpact
				},
				'Risk',
				result.data.createRisk,
				'Stake',
				values.impactedStakes
			);
		}

		// Liens risk → openings à la création d'un risque (n-n)
		if (values.openingIds !== undefined && result.data?.createRisk?.id) {
			await syncOpeningLinks(
				{ ...result.data.createRisk, openingLinks: [] },
				values.openingIds
			);
		}

		// Création d'une opening : attacher des risques existants (n-n,
		// ajoute le lien sans toucher aux autres liens de chaque risque).
		if (values.risksId !== undefined && result.data?.createRisk?.id) {
			for (let riskId of values.risksId) {
				let oneRiskToUpdate = risks.find((r) => r.id === riskId);

				if (oneRiskToUpdate === undefined) continue;

				await syncOpeningLinks(oneRiskToUpdate, [
					...getOpeningIds(oneRiskToUpdate),
					result.data.createRisk.id
				]);
			}
		}

		// Si un scénario est actif, associer les valeurs au scénario (avec enabled: true)
		if (scenario && result.data?.createRisk?.id) {
			const risk = result.data.createRisk;

			const scenarioValues = {
				impact: values.impact || appliedDefaults.impact,
				likelihood: values.likelihood || appliedDefaults.likelihood,
				enabled: true
			};

			await optimisticlyUpdateScenario(
				risk,
				'Risk',
				scenario.id,
				scenarioValues,
				updateRiskScenario
			);
		}

		// Masquer l'élément dans tous les autres scénarios
		if (result.data?.createRisk?.id && scenarios.length > 0) {
			await hideElementInOtherScenarios(
				result.data.createRisk,
				'Risk',
				scenarios,
				scenario,
				updateRiskScenario
			);
		}

		return result;
	};

	const handleUpdateRisk = async (id, risks, scenario, values) => {
		let index = findIndex(risks, (s: any) => s.id === id);

		// Champs qui sont toujours stockés dans la baseline (non modifiables par scénario)
		const baselineFields = [
			'description',
			'comment',
			'name',
			'riskCategoryId',
			'reference',
			'isOpportunity',
			'evaluatedAt'
		];

		// Champs qui peuvent être modifiés par scénario
		const scenarioFields = ['likelihood', 'impact', 'enabled'];

		// Vérifie si on a des champs baseline à mettre à jour
		const baselineValues = pick(values, baselineFields);
		const hasBaselineChanges = Object.keys(baselineValues).length > 0;

		// Vérifie si on a des champs scénario à mettre à jour
		const scenarioValuesFromForm = pick(values, scenarioFields);
		const hasScenarioChanges =
			Object.keys(scenarioValuesFromForm).length > 0;

		// Mise à jour des champs baseline (toujours, même si un scénario est actif)
		if (hasBaselineChanges) {
			let newRisk = {
				...pick(risks[index], Object.keys(baselineValues)),
				...baselineValues
			};

			if (values.evaluatedAt !== undefined)
				newRisk.evaluatedAt =
					!newRisk.evaluatedAt ||
					!isFunction(newRisk.evaluatedAt?.toISOString)
						? ''
						: newRisk.evaluatedAt.toISOString();

			await updateRisk({
				variables: {
					id: id,
					...newRisk
				},
				update(cache) {
					cache.modify({
						id: `Risk:${id}`,
						fields: getFieldFunctions(baselineValues)
					});
				},
				optimisticResponse: {
					updateRisk: true
				}
			});
		}

		// Mise à jour des champs scénario
		if (hasScenarioChanges) {
		if (scenario) {
			// Si un scénario est actif, envoyer uniquement les champs modifiés
			await optimisticlyUpdateScenario(
				risks[index],
				'Risk',
				scenario.id,
				scenarioValuesFromForm,
				updateRiskScenario
			);
			} else {
				// Si pas de scénario, mettre à jour les valeurs baseline
				await updateRisk({
					variables: {
						id: id,
						...scenarioValuesFromForm
					},
					update(cache) {
						cache.modify({
							id: `Risk:${id}`,
							fields: getFieldFunctions(scenarioValuesFromForm)
						});
					},
					optimisticResponse: {
						updateRisk: true
					}
				});
			}
		}

		if (values.taggings !== undefined)
			await updateTaggings(risks[index], values.taggings);

		if (values.impactedStakes !== undefined) {
			console.log(
				'updating impactedStakes',
				values.impactedStakes
			);
			await riskImpactController.diff(
				{
					create: createRiskStakeImpact,
					delete: deleteRiskStakeImpact
				},
				'Risk',
				risks[index],
				'Stake',
				values.impactedStakes
			);
		}

		// Liens risk → openings (n-n) — values.openingIds (UI multi-select).
		if (values.openingIds !== undefined) {
			await syncOpeningLinks(risks[index], values.openingIds);
		}

		// Côté opening : « quels risques pointent vers moi ». On AJOUTE/RETIRE
		// ce lien sans toucher aux autres liens de chaque risque (n-n).
		if (values.risksId !== undefined) {
			const newIds = values.risksId;
			const oldIds = risks
				.filter((r) => getOpeningIds(r).includes(id))
				.map((r) => r.id);

			const idsToRemove = differenceBy(oldIds, newIds);
			const idsToAdd = differenceBy(newIds, oldIds);

			for (let riskId of idsToAdd) {
				let oneRiskToUpdate = risks.find((r) => r.id === riskId);
				if (oneRiskToUpdate === undefined) continue;
				await syncOpeningLinks(oneRiskToUpdate, [
					...getOpeningIds(oneRiskToUpdate),
					id
				]);
			}

			for (let riskId of idsToRemove) {
				let oneRiskToUpdate = risks.find((r) => r.id === riskId);
				if (oneRiskToUpdate === undefined) continue;
				await syncOpeningLinks(
					oneRiskToUpdate,
					getOpeningIds(oneRiskToUpdate).filter((oid) => oid !== id)
				);
			}
		}
	};

	const handleDelete = async (
		projectId: string,
		id: string | undefined,
		risks: any[] = []
	) => {
		console.log('deleteRisk', projectId, id);
		if (id == null) {
			return;
		}

		// Cascade des liens n-n : liens sortants du risque supprimé + liens des
		// autres risques pointant vers lui (cas suppression d'une opening).
		// Best-effort — la cascade backend (delete project) couvre le reste.
		const deleted = risks.find((r) => r.id === id);
		for (const link of deleted?.openingLinks || []) {
			await handleDeleteOpeningLink(link.id, id);
		}
		for (const other of risks) {
			if (other.id === id) continue;
			for (const link of other.openingLinks || []) {
				if (link.openingId === id) {
					await handleDeleteOpeningLink(link.id, other.id);
				}
			}
		}

		await deleteRisk({
			variables: { id: id },
			update(cache) {
				cache.modify({
					id: `Project:${projectId}`,
					fields: {
						risks(existingRisksRef, { readField }) {
							return existingRisksRef.filter(
								(ref: string) => id !== readField('id', ref)
							);
						}
					}
				});
			},

			optimisticResponse: {
				deleteRisk: true
			}
		});
	};

	return {
		createRisk: handleCreateRisk,
		updateRisk: handleUpdateRisk,
		deleteRisk: handleDelete,
		updateRiskTaggings: updateTaggings,
		getOpeningIds,
		createOpeningLink: handleCreateOpeningLink,
		deleteOpeningLink: handleDeleteOpeningLink,
		updateOpeningLinkStrength: handleUpdateOpeningLinkStrength,
		cycleOpeningLink,
		syncOpeningLinks,
		createStakeImpact: async (riskId: string, stakeId: string) => {
			console.log('createStakeImpact', riskId, stakeId);
			return await riskImpactController.create(
				createRiskStakeImpact,
				'Risk',
				riskId,
				'Stake',
				stakeId
			);
		},
		updateStakeImpactStrength: async (impact, strength) => {
			return await riskImpactController.updateStrength(
				updateRiskStakeImpactStrength,
				impact,
				'Risk',
				'Stake',
				strength
			);
		},
		deleteStakeImpact: async (id: string, riskId: string) => {
			return await riskImpactController.delete(
				deleteRiskStakeImpact,
				id,
				'Risk',
				riskId,
				'Stake'
			);
		}
	};
};
